import { useEffect, useState } from "react";
import { MENU, MENU_NOTES } from "@/data/menu";
import { api } from "@/lib/api";

export const sectionsToItems = (sections) =>
  sections.flatMap((s) => [
    ...(sections.length > 1 ? [{ heading: s.name }] : []),
    ...s.items,
  ]);

export default function Menu() {
  const [menus, setMenus] = useState(MENU);
  const [active, setActive] = useState(MENU[0].category);
  const current = menus.find((c) => c.category === active) || menus[0];

  useEffect(() => {
    api.get("/menus/brunch").then(({ data }) => {
      if (!data.sections.some((s) => s.items.length)) return;
      const brunchTab = data.title || "Brunch";
      setMenus([
        ...MENU,
        {
          category: brunchTab,
          blurb: data.note || "Brunch at Trio. Grits, chicken & waffles, and mimosas.",
          items: sectionsToItems(data.sections),
        },
      ]);
      if (new URLSearchParams(window.location.search).get("tab") === "brunch") setActive(brunchTab);
    }).catch(() => {});
  }, []);

  return (
    <section
      id="menu"
      data-testid="menu-section"
      className="trio-section py-24 lg:py-32 bg-[var(--trio-text)] text-[var(--trio-bg)]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--trio-accent)] font-bold mb-4">
              The Full Menu
            </div>
            <h2 className="font-display uppercase text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.9]">
              The Whole<br /><span className="text-[var(--trio-accent)]">Menu.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 self-end">
            <p className="text-base lg:text-lg text-[var(--trio-bg)]/70 leading-relaxed">
              Plates, sides, the bar, and what to save room for. Prices are
              honest and portions are generous.
            </p>
          </div>
        </div>

        {/* Category tabs */}
        <div
          data-testid="menu-tabs"
          className="flex flex-wrap gap-2 mb-12 border-b border-[var(--trio-bg)]/20 pb-1"
        >
          {menus.map((c) => {
            const isActive = active === c.category;
            return (
              <button
                key={c.category}
                onClick={() => setActive(c.category)}
                data-testid={`menu-tab-${c.category.toLowerCase().replace(/ & /g, "-")}`}
                className={`px-5 py-3 text-sm tracking-wide uppercase transition-all border-b-2 -mb-px ${
                  isActive
                    ? "border-[var(--trio-accent)] text-[var(--trio-accent)]"
                    : "border-transparent text-[var(--trio-bg)]/70 hover:text-[var(--trio-bg)]"
                }`}
              >
                {c.category}
              </button>
            );
          })}
        </div>

        {/* Items list */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="font-serif italic text-2xl text-[var(--trio-bg)]/80 leading-snug">
              {current.blurb}
            </p>
          </div>
          <div className="lg:col-span-8">
            <ul className="divide-y divide-[var(--trio-bg)]/15">
              {current.items.map((item, i) => item.heading ? (
                <li
                  key={i}
                  data-testid={`menu-heading-${i}`}
                  className="pt-10 pb-3 text-[11px] tracking-[0.3em] uppercase text-[var(--trio-accent)] font-bold"
                >
                  {item.heading}
                </li>
              ) : (
                <li
                  key={i}
                  data-testid={`menu-item-${i}`}
                  className="py-6 grid grid-cols-12 gap-4"
                >
                  <div className="col-span-9 sm:col-span-10">
                    <h4 className="font-display uppercase text-xl sm:text-2xl mb-1 tracking-tight">
                      {item.name}
                    </h4>
                    <p className="text-sm text-[var(--trio-bg)]/65 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                  <div className="col-span-3 sm:col-span-2 text-right">
                    {item.price ? (
                      <span className="font-display text-xl text-[var(--trio-accent)]">
                        {item.price}
                      </span>
                    ) : (
                      <span className="text-[10px] tracking-widest uppercase text-[var(--trio-bg)]/40">
                        Ask server
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
            {/* Legal / allergy notes from the printed menu */}
            <div className="mt-12 pt-8 border-t border-[var(--trio-bg)]/15 space-y-2 text-[11px] leading-relaxed text-[var(--trio-bg)]/50">
              {MENU_NOTES.map((n, i) => (
                <p key={i}>{n}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
